/**
 * Who is waiting for a parked thread create to really be written (2026-10-03).
 *
 * A create made before the workspace is known is PARKED by the thread index repository (bind-at-birth,
 * INCIDENT-20260818) and its upsert answers at once, so a caller that records "written" on that answer (the
 * guest→member adoption's marker) recorded it before anything reached the server. `whenThreadCreateWritten`
 * answers true once the create is written, false when it was dropped (archived while parked, a failed flush, a
 * change of person, the page closing first): such a caller keeps the thread pending and tries again.
 *
 * Its own module, imported by the repository (which parks and settles) and by the store (which waits), so the
 * dozens of test stand-ins for the repository keep working: with nothing parked here, the wait answers true at once,
 * which is what a stand-in's resolved upsert means. Imports nothing.
 */

const parked = new Set<string>();
const waiters = new Map<string, Array<(written: boolean) => void>>();

/** The repository parked this thread's create (a refresh of an already-parked one changes nothing). */
export function noteThreadCreateParked(threadId: string): void {
  parked.add(threadId);
}

/** Is a create parked for this thread? */
export function isThreadCreateParked(threadId: string): boolean {
  return parked.has(threadId);
}

/** The parked create was written (true) or dropped (false): every waiter hears it once. */
export function settleParkedThreadCreate(threadId: string, written: boolean): void {
  parked.delete(threadId);
  const list = waiters.get(threadId);
  if (!list) return;
  waiters.delete(threadId);
  for (const settle of list) {
    try { settle(written); } catch { /* a waiter never breaks the flush */ }
  }
}

/** Resolves true once this thread's create is written; at once when nothing is parked for it. */
export function whenThreadCreateWritten(threadId: string): Promise<boolean> {
  if (!parked.has(threadId)) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    const list = waiters.get(threadId) ?? [];
    list.push(resolve);
    waiters.set(threadId, list);
  });
}

/** Tests only. */
export function resetParkedThreadCreatesForTests(): void {
  parked.clear();
  waiters.clear();
}
